(function () {
  const STORAGE_KEY = 'dairymind-pricing-context';
  const ATTRIBUTION_KEYS = ['gclid', 'fbclid', 'msclkid', 'ttclid', 'experiment_group'];
  const PATHS = {
    ownership: { label: 'Ownership Plan', commercialModel: 'OWNERSHIP' },
    combine: { label: 'Combine Plan', commercialModel: 'COMBINE' }
  };

  function normalizePricingInput(input) {
    return {
      farmType: String(input.farmType || '').trim().toLowerCase(),
      herdSize: String(input.herdSize || '').trim()
    };
  }

  function validatePricingInput(input) {
    const normalized = normalizePricingInput(input);
    if (!normalized.farmType) return { valid: false, field: 'farmType', message: 'Choose the type of farm you operate.' };
    if (!/^\d+$/.test(normalized.herdSize)) return { valid: false, field: 'herdSize', message: 'Enter a whole number of cows.' };
    if (Number(normalized.herdSize) < 10) return { valid: false, field: 'herdSize', message: 'Your herd size must be at least 10 cows.' };
    return { valid: true, value: { farmType: normalized.farmType, herdSize: Number(normalized.herdSize) } };
  }

  function validatePricingStep(step, input) {
    if (step === 1) {
      if (!input.farmType) return { valid: false, message: 'Choose the type of farm you operate.' };
      return { valid: true };
    }
    if (step === 2) {
      const validation = validatePricingInput({
        farmType: input.farmType || 'farm',
        herdSize: input.herdSize
      });
      return validation.valid ? { valid: true } : validation;
    }
    return validatePricingInput(input);
  }

  function normalizeCollarQuantity(value) {
    const normalize = window.DairyMindPricingEngine?.normalizeCollarQuantity;
    return typeof normalize === 'function' ? normalize(value) : null;
  }

  function recommendPurchasePath(input) {
    const validation = validatePricingInput(input);
    if (!validation.valid) return validation;
    const orderQuantity = normalizeCollarQuantity(validation.value.herdSize);
    if (!Number.isInteger(orderQuantity)) {
      return { valid: false, field: 'herdSize', message: 'Pricing is temporarily unavailable. Please try again.' };
    }
    const value = Object.assign({}, validation.value, { orderQuantity: orderQuantity });
    const pathKey = value.orderQuantity < 40 ? 'ownership' : 'combine';

    return {
      valid: true,
      value: value,
      farmType: value.farmType,
      herdSize: value.herdSize,
      orderQuantity: value.orderQuantity,
      pathKey: pathKey,
      recommendedPath: PATHS[pathKey].label,
      commercialModel: PATHS[pathKey].commercialModel
    };
  }

  function buildRecommendationUrl(destination, recommendation, sourceUrl) {
    if (!destination) return '';
    const url = new URL(destination, sourceUrl || window.location.href);
    const source = new URL(sourceUrl || window.location.href);
    const data = recommendation.value;
    url.searchParams.set('farm_type', data.farmType);
    url.searchParams.set('herd_size', String(data.herdSize));
    url.searchParams.set('order_quantity', String(data.orderQuantity));
    url.searchParams.set('recommended_path', recommendation.pathKey);
    source.searchParams.forEach(function (value, key) {
      if (key.indexOf('utm_') === 0 || ATTRIBUTION_KEYS.includes(key.toLowerCase())) {
        url.searchParams.set(key, value);
      }
    });
    return url.toString();
  }

  function persistPricingContext(recommendation) {
    try {
      const existing = getPricingContext() || {};
      window.sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Object.assign({}, existing, {
          farmType: recommendation.value.farmType,
          herdSize: recommendation.value.herdSize,
          orderQuantity: recommendation.value.orderQuantity,
          recommendedPath: recommendation.pathKey,
          commercialModel: recommendation.commercialModel
        }))
      );
    } catch (error) {
      return false;
    }
    return true;
  }

  function getPricingContext() {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  function herdBucket(herdSize) {
    if (herdSize <= 50) return '10-50';
    if (herdSize < 200) return '51-199';
    return '200+';
  }

  function emitAnalytics(name, detail) {
    const payload = Object.assign({ event: name }, detail || {});
    window.dispatchEvent(new CustomEvent('dairymind:pricing', { detail: payload }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
  }

  function selectedValue(form, name) {
    return form.querySelector('input[name="' + name + '"]:checked')?.value || '';
  }

  function resetSubmissionState(root) {
    const submit = root.querySelector('[data-pricing-submit]');
    const status = root.querySelector('[data-pricing-status]');
    const herdMessage = root.querySelector('[data-pricing-herd-message]');

    if (submit) {
      submit.disabled = false;
      submit.textContent = submit.dataset.originalLabel || submit.textContent.trim();
      submit.removeAttribute('aria-busy');
    }
    if (status) status.textContent = '';
    if (herdMessage) herdMessage.textContent = '';
  }

  function initializeFinder(root) {
    const form = root.querySelector('[data-pricing-form]');
    const status = root.querySelector('[data-pricing-status]');
    const herdInput = root.querySelector('[data-pricing-herd-input]');
    const herdMessage = root.querySelector('[data-pricing-herd-message]');
    const submit = root.querySelector('[data-pricing-submit]');
    if (submit) submit.dataset.originalLabel = submit.dataset.originalLabel || submit.textContent.trim();

    function answers() {
      return {
        farmType: selectedValue(form, 'farm_type'),
        herdSize: herdInput.value
      };
    }

    function restoreContext() {
      const params = new URLSearchParams(window.location.search);
      const saved = getPricingContext();
      const context = {
        farmType: params.get('farm_type') || saved?.farmType,
        herdSize: params.get('herd_size') || saved?.herdSize
      };
      if (context.farmType) {
        const farm = form.querySelector('input[name="farm_type"][value="' + context.farmType + '"]');
        if (farm) farm.checked = true;
      }
      if (context.herdSize) herdInput.value = String(context.herdSize);
    }

    herdInput.addEventListener('input', function () {
      herdInput.value = herdInput.value.replace(/[^\d]/g, '');
      herdMessage.textContent = '';
      status.textContent = '';
    });
    submit?.addEventListener('click', function (event) {
      const recommendation = recommendPurchasePath(answers());
      if (!recommendation.valid) {
        status.textContent = recommendation.message;
        if (recommendation.field === 'herdSize') herdInput.focus();
        return;
      }
      const destination = recommendation.pathKey === 'ownership'
        ? root.dataset.ownershipUrl
        : root.dataset.combineUrl;
      if (!destination) {
        const message = recommendation.pathKey === 'ownership'
          ? 'Ownership destination URL is missing. Configure /pages/ownership in the Pricing page settings.'
          : 'Combine destination URL is missing. Configure /pages/combine in the Pricing page settings.';
        status.textContent = message;
        console.error('[DairyMindPricing]', message, recommendation);
        return;
      }
      persistPricingContext(recommendation);
      emitAnalytics('pricing_find_plan', {
        herd_size: recommendation.value.herdSize,
        herd_size_bucket: herdBucket(recommendation.value.herdSize),
        order_quantity: recommendation.value.orderQuantity,
        commercial_model: recommendation.commercialModel
      });
      emitAnalytics('pricing_recommendation_generated', {
        herd_size: recommendation.value.herdSize,
        order_quantity: recommendation.value.orderQuantity,
        recommended_path: recommendation.pathKey,
        commercial_model: recommendation.commercialModel
      });
      event.currentTarget.disabled = true;
      event.currentTarget.setAttribute('aria-busy', 'true');
      event.currentTarget.textContent = 'Finding the best setup...';
      window.setTimeout(function () {
        emitAnalytics('pricing_redirect', {
          herd_size: recommendation.value.herdSize,
          order_quantity: recommendation.value.orderQuantity,
          recommended_path: recommendation.pathKey,
          commercial_model: recommendation.commercialModel
        });
        window.location.assign(buildRecommendationUrl(destination, recommendation));
      }, 220);
    });

    restoreContext();
    emitAnalytics('pricing_view');
    emitAnalytics('pricing_finder_start');
  }

  window.DairyMindPricing = {
    normalizePricingInput: normalizePricingInput,
    validatePricingInput: validatePricingInput,
    validatePricingStep: validatePricingStep,
    recommendPurchasePath: recommendPurchasePath,
    buildRecommendationUrl: buildRecommendationUrl,
    persistPricingContext: persistPricingContext
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('[data-pricing-finder]').forEach(initializeFinder);
    });
    window.addEventListener('pageshow', function () {
      document.querySelectorAll('[data-pricing-finder]').forEach(resetSubmissionState);
    });
  }
})();
